import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'
import axios from 'axios'
import {
  PRICING_MODES,
  UNIT_OPTIONS,
  COLOR_FAMILIES,
  MATERIAL_CATEGORIES,
  CATEGORY_TO_AGGREGATE_TYPES,
  EMPTY_CREATE_FORM
} from './offeringHelpers'

const useStyles = makeStyles((theme) => ({
  errorText: {
    color: '#c62828',
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5)
  },
  inlineCreateSection: {
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(0.5)
  },
  composedTitle: {
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#1565c0'
  },
  optionChip: {
    fontSize: '0.65rem',
    height: 18,
    marginLeft: 8
  },
  showAllToggle: {
    fontSize: '0.72rem',
    textTransform: 'none',
    padding: '0 4px',
    minWidth: 0,
    marginLeft: 8
  }
}))

export default function CreateOfferingDialog ({ open, onClose, onCreated, vendors, prefillMaterialId, prefillGradationId }) {
  const classes = useStyles()

  // ── Form state ───────────────────────────────────────────────────────────────
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM)
  const [createSaving, setCreateSaving] = useState(false)
  const [createError, setCreateError] = useState('')

  // ── Material autocomplete state ──────────────────────────────────────────────
  const [materialInput, setMaterialInput] = useState('')
  const [materialOptions, setMaterialOptions] = useState([])
  const [materialLoading, setMaterialLoading] = useState(false)
  const materialDebounceRef = useRef(null)

  // ── Inline material creation state ───────────────────────────────────────────
  const [showCreateMaterial, setShowCreateMaterial] = useState(false)
  const [newMaterialColor, setNewMaterialColor] = useState('')
  const [newMaterialCategory, setNewMaterialCategory] = useState('')
  const [creatingMaterial, setCreatingMaterial] = useState(false)

  // ── Gradation autocomplete state ─────────────────────────────────────────────
  const [gradationOptions, setGradationOptions] = useState([])
  const [gradationLoading, setGradationLoading] = useState(false)
  const [showAllGradations, setShowAllGradations] = useState(false)

  // ── Location options ─────────────────────────────────────────────────────────
  const [locations, setLocations] = useState([])

  // ── Load locations once ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    axios.get('/api/aggregate-dashboard/locations?limit=200')
      .then((res) => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('[CreateOfferingDialog] locations fetch error:', err))
  }, [open])

  // ── Prefill material/gradation when provided ────────────────────────────────
  useEffect(() => {
    if (!open) return
    if (prefillMaterialId) {
      axios.get(`/api/aggregate-dashboard/materials/${prefillMaterialId}`)
        .then((res) => {
          setCreateForm((f) => ({ ...f, selectedMaterial: res.data }))
          setMaterialOptions([res.data])
        })
        .catch((err) => console.error('[CreateOfferingDialog] prefill material error:', err))
    }
    if (prefillGradationId) {
      axios.get(`/api/aggregate-dashboard/gradations/${prefillGradationId}`)
        .then((res) => {
          setCreateForm((f) => ({ ...f, selectedGradation: res.data }))
        })
        .catch((err) => console.error('[CreateOfferingDialog] prefill gradation error:', err))
    }
  }, [open, prefillMaterialId, prefillGradationId]) // eslint-disable-line

  // ── Material autocomplete — debounced search ─────────────────────────────────
  useEffect(() => {
    if (materialDebounceRef.current) clearTimeout(materialDebounceRef.current)

    if (!materialInput || materialInput.length < 2) {
      setMaterialOptions([])
      return
    }

    setMaterialLoading(true)
    materialDebounceRef.current = setTimeout(() => {
      axios.get('/api/aggregate-dashboard/materials', { params: { search: materialInput, limit: 20 } })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : []
          setMaterialOptions(data)
        })
        .catch((err) => console.error('[CreateOfferingDialog] material search error:', err))
        .finally(() => setMaterialLoading(false))
    }, 300)

    return () => { if (materialDebounceRef.current) clearTimeout(materialDebounceRef.current) }
  }, [materialInput])

  // ── Gradation options — filtered by material category ────────────────────────
  useEffect(() => {
    if (!open) return
    if (!createForm.selectedMaterial) {
      setGradationOptions([])
      return
    }

    setGradationLoading(true)
    const category = createForm.selectedMaterial.materialCategory
    const allowedTypes = showAllGradations ? null : (category ? CATEGORY_TO_AGGREGATE_TYPES[category] : null)

    if (allowedTypes && allowedTypes.length === 1) {
      axios.get('/api/aggregate-dashboard/gradations', { params: { aggregateType: allowedTypes[0] } })
        .then((res) => setGradationOptions(Array.isArray(res.data) ? res.data : []))
        .catch((err) => console.error('[CreateOfferingDialog] gradation fetch error:', err))
        .finally(() => setGradationLoading(false))
    } else {
      axios.get('/api/aggregate-dashboard/gradations')
        .then((res) => {
          const all = Array.isArray(res.data) ? res.data : []
          if (allowedTypes) {
            setGradationOptions(all.filter((g) => allowedTypes.includes(g.aggregateType)))
          } else {
            setGradationOptions(all)
          }
        })
        .catch((err) => console.error('[CreateOfferingDialog] gradation fetch error:', err))
        .finally(() => setGradationLoading(false))
    }
  }, [open, createForm.selectedMaterial, showAllGradations])

  // ── Inline material creation ─────────────────────────────────────────────────
  const handleCreateMaterial = async () => {
    if (!materialInput || materialInput.length < 2) return
    setCreatingMaterial(true)
    try {
      const res = await axios.post('/api/aggregate-dashboard/materials', {
        displayName: materialInput.trim(),
        colorFamily: newMaterialColor || 'UNKNOWN',
        materialCategory: newMaterialCategory || 'UNKNOWN'
      })
      const newMat = res.data
      setCreateForm((f) => ({ ...f, selectedMaterial: newMat, selectedGradation: null }))
      setMaterialOptions([newMat])
      setShowCreateMaterial(false)
      setNewMaterialColor('')
      setNewMaterialCategory('')
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setCreateError(msg)
    } finally {
      setCreatingMaterial(false)
    }
  }

  // ── Composed product title ───────────────────────────────────────────────────
  const composedProductTitle = createForm.selectedMaterial
    ? createForm.selectedGradation
      ? `${createForm.selectedMaterial.displayName} - ${createForm.selectedGradation.displayName}`
      : createForm.selectedMaterial.displayName
    : ''

  // ── Close handler ────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (createSaving) return
    setCreateForm(EMPTY_CREATE_FORM)
    setCreateError('')
    setMaterialInput('')
    setMaterialOptions([])
    setShowCreateMaterial(false)
    setNewMaterialColor('')
    setNewMaterialCategory('')
    setGradationOptions([])
    setShowAllGradations(false)
    onClose()
  }, [createSaving, onClose])

  const handleCreateChange = (field) => (e) => {
    setCreateForm((f) => ({ ...f, [field]: e.target.value }))
  }

  // ── Submit handler ───────────────────────────────────────────────────────────
  const handleSubmitCreate = async () => {
    if (!createForm.vendorId) { setCreateError('Vendor is required'); return }
    if (!createForm.selectedMaterial) { setCreateError('Material is required'); return }

    setCreateSaving(true)
    setCreateError('')
    try {
      const payload = {
        vendorId: createForm.vendorId,
        productTitle: composedProductTitle,
        materialProfileId: createForm.selectedMaterial._id,
        pricingMode: createForm.pricingMode || 'UNKNOWN',
        unit: createForm.unit || 'TON',
        sourceType: 'MANUAL_ENTRY'
      }
      if (createForm.supplierLocationId) payload.supplierLocationId = createForm.supplierLocationId
      if (createForm.selectedGradation) {
        payload.gradationId = createForm.selectedGradation._id
        payload.variantTitle = createForm.selectedGradation.displayName
      }
      if (createForm.price !== '') payload.price = parseFloat(createForm.price)

      await axios.post('/api/aggregate-dashboard/offerings', payload)
      setCreateForm(EMPTY_CREATE_FORM)
      setMaterialInput('')
      setMaterialOptions([])
      setShowCreateMaterial(false)
      setGradationOptions([])
      setShowAllGradations(false)
      onCreated()
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setCreateError(msg)
    } finally {
      setCreateSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Offering</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 14, paddingTop: 4 }}>
          {/* Vendor */}
          <TextField
            select
            label="Vendor *"
            value={createForm.vendorId}
            onChange={handleCreateChange('vendorId')}
            variant="outlined"
            size="small"
            fullWidth
          >
            {vendors.length === 0 && (
              <MenuItem value="" disabled>No aggregate vendors configured</MenuItem>
            )}
            {vendors.map((v) => (
              <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
            ))}
          </TextField>

          {/* Location */}
          <TextField
            select
            label="Location"
            value={createForm.supplierLocationId}
            onChange={handleCreateChange('supplierLocationId')}
            variant="outlined"
            size="small"
            fullWidth
          >
            <MenuItem value="">No specific location</MenuItem>
            {locations
              .filter((loc) => !createForm.vendorId || (loc.vendorId && String(loc.vendorId._id || loc.vendorId) === createForm.vendorId))
              .map((loc) => (
                <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
              ))}
          </TextField>

          {/* Material Autocomplete */}
          <Autocomplete
            value={createForm.selectedMaterial}
            onChange={(e, newValue) => {
              if (newValue && newValue._isCreateOption) {
                setShowCreateMaterial(true)
                return
              }
              setCreateForm((f) => ({ ...f, selectedMaterial: newValue, selectedGradation: null }))
              setShowCreateMaterial(false)
              setShowAllGradations(false)
            }}
            inputValue={materialInput}
            onInputChange={(e, newInput) => setMaterialInput(newInput)}
            options={materialOptions}
            loading={materialLoading}
            getOptionLabel={(opt) => opt.displayName || ''}
            getOptionSelected={(opt, val) => opt._id === val._id}
            filterOptions={(options) => {
              const result = [...options]
              const trimmed = materialInput.trim()
              if (trimmed.length >= 2) {
                const exactMatch = options.some((o) => o.displayName && o.displayName.toLowerCase() === trimmed.toLowerCase())
                if (!exactMatch) {
                  result.push({ _isCreateOption: true, displayName: `+ Create "${trimmed}"` })
                }
              }
              return result
            }}
            renderOption={(opt) => (
              <Box display="flex" alignItems="center" width="100%">
                <span>{opt.displayName}</span>
                {opt.materialCategory && !opt._isCreateOption && (
                  <Chip
                    label={opt.materialCategory}
                    size="small"
                    className={classes.optionChip}
                    style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
                  />
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material *"
                variant="outlined"
                size="small"
                placeholder="Search materials..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {materialLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            noOptionsText={materialInput.length < 2 ? 'Type to search...' : 'No materials found'}
            fullWidth
          />

          {/* Inline Material Creation */}
          <Collapse in={showCreateMaterial}>
            <div className={classes.inlineCreateSection}>
              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                Create new material: <strong>{materialInput}</strong>
              </Typography>
              <Box display="flex" style={{ gap: 10, marginBottom: 10 }}>
                <Autocomplete
                  value={newMaterialColor || null}
                  onChange={(e, val) => setNewMaterialColor(val || '')}
                  options={COLOR_FAMILIES}
                  renderInput={(params) => (
                    <TextField {...params} label="Color Family" variant="outlined" size="small" />
                  )}
                  size="small"
                  style={{ flex: 1 }}
                  disableClearable={false}
                />
                <Autocomplete
                  value={newMaterialCategory || null}
                  onChange={(e, val) => setNewMaterialCategory(val || '')}
                  options={MATERIAL_CATEGORIES}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" variant="outlined" size="small" />
                  )}
                  size="small"
                  style={{ flex: 1 }}
                  disableClearable={false}
                />
              </Box>
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setShowCreateMaterial(false)
                    setNewMaterialColor('')
                    setNewMaterialCategory('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleCreateMaterial}
                  disabled={creatingMaterial}
                >
                  {creatingMaterial ? <CircularProgress size={16} /> : 'Create'}
                </Button>
              </Box>
            </div>
          </Collapse>

          {/* Gradation Autocomplete */}
          <Box>
            <Box display="flex" alignItems="center" style={{ marginBottom: 2 }}>
              <Typography variant="caption" color="textSecondary">Gradation / Size</Typography>
              {createForm.selectedMaterial && (
                <Button
                  size="small"
                  className={classes.showAllToggle}
                  onClick={() => setShowAllGradations((v) => !v)}
                  color={showAllGradations ? 'primary' : 'default'}
                >
                  {showAllGradations ? 'Filtered' : 'Show All'}
                </Button>
              )}
            </Box>
            <Autocomplete
              value={createForm.selectedGradation}
              onChange={(e, newValue) => {
                setCreateForm((f) => ({
                  ...f,
                  selectedGradation: newValue,
                  unit: (newValue && newValue.defaultUnit) ? newValue.defaultUnit : f.unit
                }))
              }}
              options={gradationOptions}
              loading={gradationLoading}
              disabled={!createForm.selectedMaterial}
              getOptionLabel={(opt) => opt.displayName || ''}
              getOptionSelected={(opt, val) => opt._id === val._id}
              renderOption={(opt) => (
                <Box display="flex" alignItems="center" width="100%">
                  <span>{opt.displayName}</span>
                  {opt.sizeLabel && (
                    <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                      {opt.sizeLabel}
                    </Typography>
                  )}
                  {opt.aggregateType && (
                    <Chip
                      label={opt.aggregateType}
                      size="small"
                      className={classes.optionChip}
                      style={{ backgroundColor: '#f3e5f5', color: '#6a1b9a' }}
                    />
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder={createForm.selectedMaterial ? 'Select gradation...' : 'Select a material first'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {gradationLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              noOptionsText="No gradations available"
              fullWidth
            />
          </Box>

          {/* Composed Product Title */}
          {composedProductTitle && (
            <div className={classes.composedTitle}>
              {composedProductTitle}
            </div>
          )}

          {/* Pricing Mode */}
          <TextField
            select
            label="Pricing Mode"
            value={createForm.pricingMode}
            onChange={handleCreateChange('pricingMode')}
            variant="outlined"
            size="small"
            fullWidth
          >
            {PRICING_MODES.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>

          {/* Price + Unit */}
          <Box display="flex" style={{ gap: 10 }}>
            <TextField
              label="Price"
              value={createForm.price}
              onChange={handleCreateChange('price')}
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              placeholder="0.00"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              select
              label="Unit"
              value={createForm.unit}
              onChange={handleCreateChange('unit')}
              variant="outlined"
              size="small"
              style={{ minWidth: 90 }}
            >
              {UNIT_OPTIONS.map((u) => (
                <MenuItem key={u} value={u}>{u}</MenuItem>
              ))}
            </TextField>
          </Box>

          {createError && (
            <Typography className={classes.errorText}>{createError}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createSaving}>Cancel</Button>
        <Button
          onClick={handleSubmitCreate}
          color="primary"
          variant="contained"
          disabled={createSaving}
        >
          {createSaving ? <CircularProgress size={18} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
